import { Component, type ErrorInfo, type ReactNode } from 'react';

import { crashReport, reportError, type ErrorRecord } from '../lib/errors.ts';

type Props = {
  children: ReactNode;
  /** What the boundary protects, for the message: "chart", "trade page", "app" */
  area: string;
};
type State = { error?: Error; rec?: ErrorRecord };

/**
 * Keeps a render error from blanking the whole app: React unmounts everything under the nearest boundary,
 * and without one that is the page. The fallback names the error, offers a retry and a reload, and holds
 * a report ready to copy. The error also goes to the console and the session log (lib/errors.ts).
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = {};

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    (error as Error & { __papurrSeen?: boolean }).__papurrSeen = true;
    this.setState({ rec: reportError(`${this.props.area} render`, error, info.componentStack ?? undefined) });
  }

  render(): ReactNode {
    const { error, rec } = this.state;
    if (!error) return this.props.children;
    const report = rec ? crashReport(rec) : `${error.name}: ${error.message}\n${error.stack ?? ''}`;
    return (
      <div className="crash panel" role="alert">
        <h2>Something went wrong</h2>
        <p>
          The {this.props.area} hit an error and stopped drawing. Funds and positions live onchain and are not
          affected: this is a display problem.
        </p>
        <code className="crash-msg">{error.message || error.name}</code>
        <div className="crash-actions">
          <button className="btn btn-primary btn-sm" onClick={() => this.setState({ error: undefined, rec: undefined })}>
            Try again
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => location.reload()}>
            Reload the app
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => void navigator.clipboard?.writeText(report)}>
            Copy the report
          </button>
        </div>
        <details className="crash-details">
          <summary>Details</summary>
          <pre>{report}</pre>
        </details>
      </div>
    );
  }
}
